// src/v3/ui/app.tsx

import { QueryClient, QueryClientProvider, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { createBrowserRouter, RouterProvider } from "react-router-dom";
import { z } from "zod";
import { PORTFOLIO_PRICE_SYNC_EVENT, PORTFOLIO_PRICE_SYNC_STATUS, PORTFOLIO_PRICE_UPDATED_EVENT, isPortfolioPriceSyncActive } from "@signals/shared";
import { AppTableProvider, Card, CardContent, CardHeader, CardTitle, RootLayout, apiFetch, qk, useEvents } from "./_common";
import { BacktesterPage } from "./backtester";
import { DatasourcePage } from "./datasource";
import { IdeasPage } from "./ideas";
import { McpSettingsPage } from "./mcp";
import { PinbarPage } from "./pinbar";
import { PortfolioPage } from "./portfolio";
import { SchedulerPage } from "./scheduler";
import { SymbolPage } from "./symbol";

function DashboardPage() {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Dashboard</CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-muted-foreground">
          Welcome to SignalsApp. Select a feature from the sidebar.
        </p>
      </CardContent>
    </Card>
  );
}

const router = createBrowserRouter([
  {
    path: "/",
    element: <RootLayout />,
    children: [
      { index: true, element: <DashboardPage /> },
      { path: "signals", element: <PinbarPage /> },
      { path: "symbols", element: <SymbolPage /> },
      { path: "portfolio", element: <PortfolioPage /> },
      { path: "settings/mcp", element: <McpSettingsPage /> },
      { path: "ideas", element: <IdeasPage /> },
      { path: "backtester", element: <BacktesterPage /> },
      { path: "scheduler", element: <SchedulerPage /> },
      { path: "datasource", element: <DatasourcePage /> },
    ],
  },
]);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
    },
  },
});

const portfolioPriceUpdatedPayloadSchema = z.object({
  portfolioIds: z.array(z.string().uuid()),
  symbolId: z.string().uuid(),
  priceDate: z.string().date(),
});
const portfolioPriceSyncPayloadSchema = z.object({
  operationId: z.string(),
  portfolioId: z.string().uuid(),
  status: z.enum(PORTFOLIO_PRICE_SYNC_STATUS),
});

export function PortfolioPriceEventsHandler() {
  const queryClient = useQueryClient();
  const { events, isConnected } = useEvents([PORTFOLIO_PRICE_UPDATED_EVENT, PORTFOLIO_PRICE_SYNC_EVENT]);
  const processedCount = useRef(0);
  const activeOperations = useRef(new Map<string, {
    portfolioId: string;
    timer: ReturnType<typeof setTimeout> | null;
    inFlight: boolean;
  }>());
  const wasConnected = useRef(false);

  const refreshPortfolio = (portfolioId: string) => {
    void Promise.all([
      queryClient.invalidateQueries({ queryKey: qk.portfolio.positionsForPortfolio(portfolioId) }),
      queryClient.invalidateQueries({ queryKey: qk.portfolio.summaryForPortfolio(portfolioId) }),
      queryClient.invalidateQueries({ queryKey: qk.portfolio.snapshots(portfolioId) }),
      queryClient.invalidateQueries({ queryKey: qk.portfolio.compareForPortfolio(portfolioId) }),
      queryClient.invalidateQueries({ queryKey: qk.portfolio.yearlyPerformance.all(portfolioId) }),
      queryClient.invalidateQueries({ queryKey: qk.portfolio.yearlyPerformance.singleForPortfolio(portfolioId) }),
    ]);
  };

  const pollOperation = (operationId: string, delayMs = 2000) => {
    const tracked = activeOperations.current.get(operationId);
    if (!tracked || tracked.timer || tracked.inFlight) return;
    tracked.timer = setTimeout(() => {
      tracked.timer = null;
      tracked.inFlight = true;
      void apiFetch<{ operationId: string; portfolioId: string; status: z.infer<typeof portfolioPriceSyncPayloadSchema>["status"] }>(
        `/api/portfolio/price-sync/${encodeURIComponent(operationId)}?portfolioId=${encodeURIComponent(tracked.portfolioId)}`,
      ).then((status) => {
        if (activeOperations.current.get(operationId) !== tracked) return;
        tracked.inFlight = false;
        if (isPortfolioPriceSyncActive(status.status)) {
          pollOperation(operationId);
          return;
        }
        activeOperations.current.delete(operationId);
        refreshPortfolio(tracked.portfolioId);
      }).catch(() => {
        if (activeOperations.current.get(operationId) !== tracked) return;
        tracked.inFlight = false;
        // Keep checking after a transient network failure or while the server is reconnecting.
        pollOperation(operationId);
      });
    }, delayMs);
  };

  useEffect(() => {
    if (isConnected && !wasConnected.current) {
      void queryClient.invalidateQueries({ queryKey: qk.portfolio.all });
      for (const [operationId, operation] of activeOperations.current) {
        if (operation.timer) clearTimeout(operation.timer);
        operation.timer = null;
        pollOperation(operationId, 0);
      }
    }
    wasConnected.current = isConnected;

    const newEvents = events.slice(processedCount.current);
    processedCount.current = events.length;

    for (const event of newEvents) {
      if (event.type === PORTFOLIO_PRICE_SYNC_EVENT) {
        const parsed = portfolioPriceSyncPayloadSchema.safeParse(event.data);
        if (!parsed.success) continue;
        const portfolioId = parsed.data.portfolioId;
        if (isPortfolioPriceSyncActive(parsed.data.status)) {
          const prior = activeOperations.current.get(parsed.data.operationId);
          if (prior?.timer) clearTimeout(prior.timer);
          activeOperations.current.set(parsed.data.operationId, { portfolioId, timer: null, inFlight: false });
          pollOperation(parsed.data.operationId);
        } else {
          const prior = activeOperations.current.get(parsed.data.operationId);
          if (prior?.timer) clearTimeout(prior.timer);
          activeOperations.current.delete(parsed.data.operationId);
          refreshPortfolio(portfolioId);
        }
        continue;
      }
      if (event.type !== PORTFOLIO_PRICE_UPDATED_EVENT) continue;
      const parsed = portfolioPriceUpdatedPayloadSchema.safeParse(event.data);
      if (!parsed.success) continue;

      for (const portfolioId of new Set(parsed.data.portfolioIds)) {
        void Promise.all([
          queryClient.invalidateQueries({ queryKey: qk.portfolio.positionsForPortfolio(portfolioId) }),
          queryClient.invalidateQueries({ queryKey: qk.portfolio.summaryForPortfolio(portfolioId) }),
          queryClient.invalidateQueries({ queryKey: qk.portfolio.snapshots(portfolioId) }),
          queryClient.invalidateQueries({ queryKey: qk.portfolio.compareForPortfolio(portfolioId) }),
        ]);
      }
    }
  }, [events, isConnected, queryClient]);

  useEffect(() => () => {
    for (const operation of activeOperations.current.values()) {
      if (operation.timer) clearTimeout(operation.timer);
    }
    activeOperations.current.clear();
  }, []);

  return null;
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <PortfolioPriceEventsHandler />
      <AppTableProvider>
        <RouterProvider router={router} />
      </AppTableProvider>
    </QueryClientProvider>
  );
}
